/**
 * A minimal QR encoder — byte mode, error-correction level M, versions 1–10.
 *
 * WHY THIS EXISTS instead of `npm i qrcode`. The one thing this code draws is a
 * ONE-TIME LOGIN CREDENTIAL (AH4, "sign in on another device"). Pulling a
 * third-party package into the bundle that renders it — let alone a CDN script,
 * which the CSP forbids anyway — means a supply-chain update can read a live
 * sign-in link. So the encoder is here, in ~300 lines you can read in one sitting,
 * and it does exactly one thing.
 *
 * SCOPE, deliberately small: byte mode only (a URL is bytes), level M only (~15%
 * recovery — the sweet spot for a code photographed off a screen at an angle), and
 * versions 1–10 (up to 213 bytes, roughly twice the longest link we mint). Anything
 * longer throws rather than silently producing a code no phone can read; the caller
 * falls back to showing the link as text.
 *
 * The output is a boolean matrix — `true` is a dark module. Rendering is the
 * caller's business (we draw it as an SVG path, which stays crisp at any zoom and
 * needs no canvas).
 *
 * Internally everything is a typed array indexed through small helpers. That is not
 * micro-optimisation: this project compiles with `noUncheckedIndexedAccess`, and a
 * bit-placement routine written as `number[]` turns into a hundred `!` assertions,
 * every one of which is a place a real off-by-one could hide in plain sight.
 *
 * Verified against a real decoder, not just against itself: every version 1–10 plus
 * a sweep of random login links was rendered to PNG and read back with OpenCV's QR
 * detector (AH4, 32/32). An encoder that only agrees with its own unit tests is an
 * encoder that has never been scanned.
 *
 * VENDORED, verbatim, from the workspace shell (`workspace/src/qrcode.ts`). The two
 * repos share no package, and inventing one to carry 300 dependency-free lines would
 * cost more than the duplication does — but the copies must stay identical: fix a bug
 * HERE and carry it THERE, and vice versa. `qrcode.test.ts` in each repo pins the same
 * properties, so a divergence shows up as a failing test rather than as a code that
 * scans on the dashboard and not in the workspace.
 */

interface VersionSpec {
  /** EC codewords per block. */
  ec: number;
  /** Block groups: [number of blocks, data codewords in each]. */
  groups: ReadonlyArray<readonly [number, number]>;
}

/** Level-M block structure, indexed by `version - 1`. */
const VERSIONS: readonly VersionSpec[] = [
  { ec: 10, groups: [[1, 16]] },
  { ec: 16, groups: [[1, 28]] },
  { ec: 26, groups: [[1, 44]] },
  { ec: 18, groups: [[2, 32]] },
  { ec: 24, groups: [[2, 43]] },
  { ec: 16, groups: [[4, 27]] },
  { ec: 18, groups: [[4, 31]] },
  { ec: 22, groups: [[2, 38], [2, 39]] },
  { ec: 22, groups: [[3, 36], [2, 37]] },
  { ec: 26, groups: [[4, 43], [1, 44]] },
];

/** Centres of the alignment patterns, indexed by `version - 1` (version 1 has none). */
const ALIGNMENT: ReadonlyArray<readonly number[]> = [
  [], [6, 18], [6, 22], [6, 26], [6, 30],
  [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
];

const MAX_VERSION = VERSIONS.length;

function specFor(version: number): VersionSpec {
  const spec = VERSIONS[version - 1];
  if (!spec) throw new Error(`qrcode: no such version ${version}`);
  return spec;
}

/** Data codewords the version holds (all groups summed). */
function dataCapacity(spec: VersionSpec): number {
  return spec.groups.reduce((n, g) => n + g[0] * g[1], 0);
}

/**
 * Read one byte of a typed array.
 *
 * `noUncheckedIndexedAccess` types every element read as `number | undefined`, including a typed
 * array's, so the arithmetic below would otherwise be a field of `!` assertions — and a `!` in a
 * bit-placement routine is exactly where a genuine off-by-one hides in plain sight. This says the
 * same thing once, out loud, and reads an out-of-range index as 0: every caller here indexes with
 * a loop counter it just bounded, so the fallback is unreachable rather than a papered-over bug.
 */
const at = (a: Uint8Array, i: number): number => a[i] ?? 0;

// --- GF(256) ---------------------------------------------------------------
// Reed–Solomon over the QR field (primitive polynomial 0x11d). Log/antilog tables
// turn every multiplication in the EC step into two lookups and an add.
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = at(EXP, i - 255);
})();

const mul = (a: number, b: number): number =>
  a === 0 || b === 0 ? 0 : at(EXP, at(LOG, a) + at(LOG, b));

/** The generator polynomial for `degree` EC codewords: ∏ (x − α^i). */
function generatorPoly(degree: number): Uint8Array {
  let poly = Uint8Array.of(1);
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j++) {
      next[j] = at(next, j) ^ at(poly, j);
      next[j + 1] = at(next, j + 1) ^ mul(at(poly, j), at(EXP, i));
    }
    poly = next;
  }
  return poly;
}

/** Polynomial long division — the remainder IS the error-correction block. */
function ecCodewords(data: Uint8Array, count: number): Uint8Array {
  const gen = generatorPoly(count);
  const rem = new Uint8Array(count);
  for (let d = 0; d < data.length; d++) {
    const factor = at(data, d) ^ at(rem, 0);
    rem.copyWithin(0, 1);
    rem[count - 1] = 0;
    if (factor !== 0) {
      for (let i = 0; i < count; i++) rem[i] = at(rem, i) ^ mul(at(gen, i + 1), factor);
    }
  }
  return rem;
}

// --- bit stream ------------------------------------------------------------
class BitBuffer {
  private bits: number[] = [];

  put(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i--) this.bits.push((value >>> i) & 1);
  }

  get length(): number {
    return this.bits.length;
  }

  padTo(capacityBits: number): void {
    // Terminator (up to four 0s), then pad to a byte boundary. The alternating pad BYTES are added
    // by the caller, which knows how many codewords the version wants.
    for (let i = 0; i < 4 && this.bits.length < capacityBits; i++) this.bits.push(0);
    while (this.bits.length % 8 !== 0) this.bits.push(0);
  }

  toBytes(): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.bits.length; i += 8) {
      let byte = 0;
      for (let j = 0; j < 8; j++) byte = (byte << 1) | (this.bits[i + j] ?? 0);
      out.push(byte);
    }
    return out;
  }
}

/** Smallest version whose level-M byte capacity holds `length` bytes. */
function pickVersion(length: number): number {
  for (let v = 1; v <= MAX_VERSION; v++) {
    // 4 mode bits + the char-count field (8 bits below version 10, 16 from 10 up).
    const headerBits = 4 + (v < 10 ? 8 : 16);
    if (dataCapacity(specFor(v)) * 8 - headerBits >= length * 8) return v;
  }
  throw new Error(`qrcode: ${length} bytes exceeds the version-${MAX_VERSION} capacity of this encoder`);
}

/** Data codewords, split into blocks, EC-ed, and interleaved as the spec orders them. */
function codewordsFor(version: number, bytes: Uint8Array): Uint8Array {
  const spec = specFor(version);
  const buf = new BitBuffer();
  buf.put(0b0100, 4); // byte mode
  buf.put(bytes.length, version < 10 ? 8 : 16);
  for (const b of bytes) buf.put(b, 8);

  const totalData = dataCapacity(spec);
  buf.padTo(totalData * 8);
  const data = buf.toBytes();
  const PAD = [0xec, 0x11] as const;
  for (let i = 0; data.length < totalData; i++) data.push(PAD[i % 2] ?? 0);

  const dataBlocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;
  for (const [blocks, size] of spec.groups) {
    for (let b = 0; b < blocks; b++) {
      const block = Uint8Array.from(data.slice(offset, offset + size));
      offset += size;
      dataBlocks.push(block);
      ecBlocks.push(ecCodewords(block, spec.ec));
    }
  }

  // Interleave: the i-th codeword of every block in turn, data first, then EC. This is what makes
  // a scratch across the printed code damage a little of every block instead of all of one.
  const out: number[] = [];
  const maxData = dataBlocks.reduce((n, b) => Math.max(n, b.length), 0);
  for (let i = 0; i < maxData; i++) {
    for (const block of dataBlocks) if (i < block.length) out.push(at(block, i));
  }
  for (let i = 0; i < spec.ec; i++) {
    for (const block of ecBlocks) out.push(at(block, i));
  }
  return Uint8Array.from(out);
}

// --- matrix ----------------------------------------------------------------
/**
 * The module grid: one byte per module in `dark`, one in `fixed` ("this is a function pattern —
 * data placement steps over it and the mask leaves it alone"). Flat typed arrays rather than
 * `boolean[][]` so every access below is a plain `number` (see the module docstring).
 */
class Grid {
  readonly size: number;
  readonly dark: Uint8Array;
  readonly fixed: Uint8Array;

  constructor(size: number) {
    this.size = size;
    this.dark = new Uint8Array(size * size);
    this.fixed = new Uint8Array(size * size);
  }

  at(r: number, c: number): number {
    return at(this.dark, r * this.size + c);
  }

  isFixed(r: number, c: number): boolean {
    return at(this.fixed, r * this.size + c) === 1;
  }

  /** Set a module. `fixed` marks it a function pattern (the default — everything drawn here is). */
  set(r: number, c: number, dark: boolean, fixed = true): void {
    if (r < 0 || c < 0 || r >= this.size || c >= this.size) return;
    const i = r * this.size + c;
    this.dark[i] = dark ? 1 : 0;
    if (fixed) this.fixed[i] = 1;
  }

  /** Set a module's colour WITHOUT touching its fixed flag (data placement, masking, format bits). */
  paint(r: number, c: number, dark: boolean): void {
    this.dark[r * this.size + c] = dark ? 1 : 0;
  }

  toMatrix(): boolean[][] {
    const rows: boolean[][] = [];
    for (let r = 0; r < this.size; r++) {
      const row: boolean[] = [];
      for (let c = 0; c < this.size; c++) row.push(this.at(r, c) === 1);
      rows.push(row);
    }
    return rows;
  }
}

function placeFinder(g: Grid, row: number, col: number): void {
  // The 7×7 eye plus its one-module white separator — drawn together so the separator cannot be
  // forgotten and leave the eye touching data (the classic "scans on one phone only" bug).
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const inRing =
        (r >= 0 && r <= 6 && (c === 0 || c === 6)) || (c >= 0 && c <= 6 && (r === 0 || r === 6));
      const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      g.set(row + r, col + c, inRing || inCore);
    }
  }
}

function placeFunctionPatterns(g: Grid, version: number): void {
  const n = g.size;
  placeFinder(g, 0, 0);
  placeFinder(g, 0, n - 7);
  placeFinder(g, n - 7, 0);

  for (let i = 8; i < n - 8; i++) {
    const dark = i % 2 === 0;
    g.set(6, i, dark);
    g.set(i, 6, dark);
  }

  const centres = ALIGNMENT[version - 1] ?? [];
  for (const r of centres) {
    for (const c of centres) {
      // Skipped where an alignment pattern would collide with a finder eye.
      if ((r === 6 && c === 6) || (r === 6 && c === n - 7) || (r === n - 7 && c === 6)) continue;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          g.set(r + dr, c + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1);
        }
      }
    }
  }

  g.set(n - 8, 8, true); // the always-dark module

  // Reserve the format areas so data placement steps over them.
  for (let i = 0; i < 9; i++) {
    if (!g.isFixed(8, i)) g.set(8, i, false);
    if (!g.isFixed(i, 8)) g.set(i, 8, false);
  }
  for (let i = 0; i < 8; i++) {
    if (!g.isFixed(8, n - 1 - i)) g.set(8, n - 1 - i, false);
    if (!g.isFixed(n - 1 - i, 8)) g.set(n - 1 - i, 8, false);
  }

  if (version >= 7) {
    // Version information: BCH(18,6) over the version number, in two 3×6 blocks.
    let bits = version << 12;
    for (let i = 0; i < 6; i++) {
      if ((bits >>> (17 - i)) & 1) bits ^= 0x1f25 << (5 - i);
    }
    const value = (version << 12) | (bits & 0xfff);
    for (let i = 0; i < 18; i++) {
      const bit = ((value >>> i) & 1) === 1;
      g.set(Math.floor(i / 3), n - 11 + (i % 3), bit);
      g.set(n - 11 + (i % 3), Math.floor(i / 3), bit);
    }
  }
}

/** Zigzag the codewords up and down the two-module-wide columns, right to left. */
function placeData(g: Grid, codewords: Uint8Array): void {
  const n = g.size;
  let bitIndex = 0;
  let upward = true;
  for (let right = n - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // the vertical timing column is not part of any data column
    for (let step = 0; step < n; step++) {
      const row = upward ? n - 1 - step : step;
      for (const col of [right, right - 1]) {
        if (g.isFixed(row, col)) continue;
        const byte = at(codewords, bitIndex >>> 3);
        g.paint(row, col, ((byte >>> (7 - (bitIndex & 7))) & 1) === 1);
        bitIndex++;
      }
    }
    upward = !upward;
  }
}

const MASKS: ReadonlyArray<(r: number, c: number) => boolean> = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

function maskFn(index: number): (r: number, c: number) => boolean {
  const fn = MASKS[index];
  if (!fn) throw new Error(`qrcode: no such mask ${index}`);
  return fn;
}

/** The spec's four penalty rules — a lower score is a code a camera finds easier. */
function penalty(g: Grid): number {
  const n = g.size;
  let score = 0;

  const runScore = (read: (i: number) => number): number => {
    let total = 0;
    let len = 1;
    for (let i = 1; i < n; i++) {
      if (read(i) === read(i - 1)) len++;
      else {
        if (len >= 5) total += 3 + (len - 5);
        len = 1;
      }
    }
    return len >= 5 ? total + 3 + (len - 5) : total;
  };
  for (let r = 0; r < n; r++) score += runScore((i) => g.at(r, i));
  for (let c = 0; c < n; c++) score += runScore((i) => g.at(i, c));

  for (let r = 0; r < n - 1; r++) {
    for (let c = 0; c < n - 1; c++) {
      const v = g.at(r, c);
      if (v === g.at(r, c + 1) && v === g.at(r + 1, c) && v === g.at(r + 1, c + 1)) score += 3;
    }
  }

  // The 1:1:3:1:1 finder-lookalike, in both directions.
  const PATTERN = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0];
  const REVERSE = [...PATTERN].reverse();
  const matches = (read: (i: number) => number, at: number, pat: number[]): boolean =>
    pat.every((v, k) => read(at + k) === v);
  for (let r = 0; r < n; r++) {
    const read = (i: number) => g.at(r, i);
    for (let c = 0; c + 11 <= n; c++) {
      if (matches(read, c, PATTERN) || matches(read, c, REVERSE)) score += 40;
    }
  }
  for (let c = 0; c < n; c++) {
    const read = (i: number) => g.at(i, c);
    for (let r = 0; r + 11 <= n; r++) {
      if (matches(read, r, PATTERN) || matches(read, r, REVERSE)) score += 40;
    }
  }

  let darkCount = 0;
  for (let i = 0; i < g.dark.length; i++) darkCount += at(g.dark, i);
  score += Math.floor(Math.abs((darkCount * 100) / (n * n) - 50) / 5) * 10;
  return score;
}

/** Format information: 5 bits (level M + mask) → BCH(15,5) → the fixed XOR mask. */
function formatBits(mask: number): number {
  const data = (0b00 << 3) | mask; // 0b00 = level M
  let bits = data << 10;
  for (let i = 0; i < 5; i++) {
    if ((bits >>> (14 - i)) & 1) bits ^= 0x537 << (4 - i);
  }
  return ((data << 10) | (bits & 0x3ff)) ^ 0x5412;
}

function placeFormat(g: Grid, mask: number): void {
  const n = g.size;
  const bits = formatBits(mask);
  for (let i = 0; i < 15; i++) {
    // MSB first: step 0 carries bit 14. Reading it the other way round produces a code whose data
    // area is byte-perfect and whose format block is a mirror image — which decodes as nothing at
    // all, on every scanner, with no clue as to why.
    const bit = ((bits >>> (14 - i)) & 1) === 1;
    // Copy 1: around the top-left eye, stepping over the timing row/column.
    if (i < 6) g.paint(8, i, bit);
    else if (i === 6) g.paint(8, 7, bit);
    else if (i === 7) g.paint(8, 8, bit);
    else if (i === 8) g.paint(7, 8, bit);
    else g.paint(14 - i, 8, bit);
    // Copy 2: split between the other two eyes, so a damaged corner still yields the format. Seven
    // bits go up the left column and EIGHT along the top row — the asymmetry is the always-dark
    // module at (n-8, 8) sitting in the middle of what would otherwise be a run of eight.
    if (i < 7) g.paint(n - 1 - i, 8, bit);
    else g.paint(8, n - 15 + i, bit);
  }
}

/**
 * Encode `text` as a QR matrix (level M). `true` = a dark module.
 *
 * Throws when the text does not fit in version 10 — the caller should then show the
 * link as text rather than draw a code nothing can read.
 */
export function encodeQR(text: string): boolean[][] {
  const bytes = new TextEncoder().encode(text);
  const version = pickVersion(bytes.length);
  const codewords = codewordsFor(version, bytes);

  let best: Grid | null = null;
  let bestScore = Infinity;
  for (let mask = 0; mask < MASKS.length; mask++) {
    const g = new Grid(version * 4 + 17);
    placeFunctionPatterns(g, version);
    placeData(g, codewords);
    const apply = maskFn(mask);
    for (let r = 0; r < g.size; r++) {
      for (let c = 0; c < g.size; c++) {
        if (!g.isFixed(r, c) && apply(r, c)) g.paint(r, c, g.at(r, c) === 0);
      }
    }
    placeFormat(g, mask);
    const score = penalty(g);
    if (score < bestScore) {
      best = g;
      bestScore = score;
    }
  }
  if (!best) throw new Error('qrcode: no mask produced a matrix');
  return best.toMatrix();
}

/**
 * The matrix as a standalone SVG string, quiet zone included.
 *
 * One `<path>` of little squares rather than a rect per module: a version-6 code is
 * ~1700 modules, and 1700 DOM nodes inside a dialog is a visible hitch on a phone.
 *
 * The quiet zone is FIVE modules, one more than the standard's minimum of four. That
 * is not caution for its own sake: in the AH4 decoder sweep one link in 32 failed to
 * detect at a 4-module margin and read perfectly at 5 — and OpenCV's own encoder
 * failed on that same link at 4, so it is the detector's margin, not this encoder's
 * output. One module of white is cheaper than a scan that does not take.
 */
export function qrSvg(text: string, opts: { size?: number; quiet?: number } = {}): string {
  const dark = encodeQR(text);
  const n = dark.length;
  const quiet = opts.quiet ?? 5;
  const span = n + quiet * 2;
  const px = opts.size ?? 220;
  let path = '';
  for (let r = 0; r < n; r++) {
    const row = dark[r] ?? [];
    for (let c = 0; c < n; c++) {
      if (row[c]) path += `M${c + quiet} ${r + quiet}h1v1h-1z`;
    }
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}" ` +
    `viewBox="0 0 ${span} ${span}" shape-rendering="crispEdges">` +
    `<rect width="${span}" height="${span}" fill="#fff"/>` +
    `<path d="${path}" fill="#000"/></svg>`
  );
}
